import { useState, useRef, useEffect, useCallback } from 'react';

export function MentionTextarea({ value, onChange, characters, placeholder, className, testId, onMention }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  const filtered = characters.filter(c =>
    c.name.toLowerCase().includes(query.toLowerCase())
  );

  const getCaretCoordinates = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return { top: 0, left: 0 };
    const mirror = document.createElement('div');
    const style = window.getComputedStyle(ta);
    const props = [
      'fontFamily', 'fontSize', 'fontWeight', 'lineHeight', 'letterSpacing',
      'wordSpacing', 'textIndent', 'paddingTop', 'paddingLeft', 'paddingRight',
      'borderTopWidth', 'borderLeftWidth', 'boxSizing', 'whiteSpace', 'wordWrap', 'overflowWrap'
    ];
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.overflow = 'hidden';
    mirror.style.width = style.width;
    props.forEach(p => mirror.style[p] = style[p]);
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.wordWrap = 'break-word';

    const text = ta.value.substring(0, ta.selectionEnd);
    mirror.textContent = text;
    const span = document.createElement('span');
    span.textContent = '|';
    mirror.appendChild(span);
    document.body.appendChild(mirror);

    const rect = ta.getBoundingClientRect();
    const spanRect = span.getBoundingClientRect();
    const mirrorRect = mirror.getBoundingClientRect();

    const top = spanRect.top - mirrorRect.top - ta.scrollTop + 24;
    const left = spanRect.left - mirrorRect.left;

    document.body.removeChild(mirror);
    return { top: Math.min(top, rect.height - 10), left: Math.min(left, rect.width - 200) };
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(val);

    // Check if we're in a mention context
    const textBefore = val.substring(0, cursorPos);
    const atIdx = textBefore.lastIndexOf('@');

    if (atIdx !== -1) {
      const textBetween = textBefore.substring(atIdx + 1);
      // Only show if no space before @ (or @ is at start) and no newline in between
      const charBefore = atIdx > 0 ? val[atIdx - 1] : ' ';
      if ((charBefore === ' ' || charBefore === '\n' || atIdx === 0) && !textBetween.includes('\n')) {
        setQuery(textBetween);
        setMentionStart(atIdx);
        setShowDropdown(true);
        setSelectedIdx(0);
        const coords = getCaretCoordinates();
        setDropdownPos(coords);
        return;
      }
    }
    setShowDropdown(false);
  };

  const insertMention = (char) => {
    const ta = textareaRef.current;
    if (!ta || mentionStart === -1) return;
    const before = value.substring(0, mentionStart);
    const after = value.substring(ta.selectionStart);
    const newVal = `${before}@${char.name} ${after}`;
    onChange(newVal);
    setShowDropdown(false);
    setQuery('');
    setMentionStart(-1);

    if (onMention) onMention(char);

    // Restore cursor position after the mention
    requestAnimationFrame(() => {
      const pos = mentionStart + char.name.length + 2; // @name + space
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(prev => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(filtered[selectedIdx]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          textareaRef.current && !textareaRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const items = dropdownRef.current.children;
      if (items[selectedIdx]) {
        items[selectedIdx].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIdx, showDropdown]);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        data-testid={testId}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        className={className}
        placeholder={placeholder}
      />
      {showDropdown && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          data-testid={`${testId}-mention-dropdown`}
          className="absolute z-50 w-56 max-h-48 overflow-y-auto rounded-lg border border-white/10 bg-[#1A1A20] shadow-xl"
          style={{ top: dropdownPos.top, left: Math.max(0, dropdownPos.left) }}
        >
          {filtered.map((char, idx) => (
            <button
              key={char.id}
              type="button"
              data-testid={`mention-char-${char.id}`}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                idx === selectedIdx
                  ? 'bg-[#FF4500]/15 text-[#FF4500]'
                  : 'text-[#c8c8d0] hover:bg-white/5'
              }`}
              onMouseEnter={() => setSelectedIdx(idx)}
              onMouseDown={(e) => { e.preventDefault(); insertMention(char); }}
            >
              <span className="text-[#FF4500] font-medium">@</span>
              <span className="truncate">{char.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
